import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Save, X, FileText, Plus, Trash2, Ship, User, MapPin, Package,
    Info, ChevronDown, ChevronUp, Anchor, CreditCard, Printer,
    Building2, Globe, Calendar, Hash, Check, Upload, Settings, Route,
    Container, Weight, Ruler, ArrowLeft, Send
} from 'lucide-react';
import { useDispatch, useSelector } from "react-redux";
import { GetMBLDetails, updateMBLDetails } from '@/redux/Booking/actions';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SearchBar } from '@/components/SearchBox/SearchBar';
import { getAddressAPI, getAddresswithfulldetail } from '@/redux/Purchase/actions';
import DateInputwithcal from '@/components/ui/DateInputwithcal';
import FileUpload from '@/components/FileUpload/FileUpload';
import { validateForm } from "@/components/ui/useFormValidation";
import UploadNotes from '@/components/NotesUpload/NotesUpload';
import { toast } from 'sonner';
import ValidationMBLfrom from '@/validation/MBLfrom.json';
import { getClauselist } from '@/redux/Common/actions';
import { callLoader } from '@/redux/Loader/actions';
import PageHeader from "@/components/FormHeader/formPageHeader";
import { InlineCountryFlag } from '@/components/shipment/CountryBadge';

const SECTION_COLORS = {
    blue: "bg-blue-500",
    cyan: "bg-cyan-500",
    violet: "bg-violet-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
    teal: "bg-teal-500",
    green: "bg-emerald-500",
    indigo: "bg-indigo-500",
    slate: "bg-slate-500",
    pink: "bg-pink-600",
};

const SectionCard = React.forwardRef(({ children, className = "" }, ref) => (
    <div
        ref={ref}
        className={`bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200 ${className}`}
    >
        {children}
    </div>
));

const SectionHeader = ({
    icon: Icon,
    title,
    color = "blue",
    section,
    badge,
    expandedSections,
    toggleSection,
}) => {
    const iconBg = SECTION_COLORS[color] ?? SECTION_COLORS.blue;
    return (
        <button
            type="button"
            onClick={() => toggleSection(section)}
            className="w-full flex items-center justify-between p-4 border-b bg-gradient-to-r to-slate-100 dark:from-slate-800 dark:to-slate-700 cursor-pointer"
        >
            <div className="flex items-center gap-3">
                <div className={`p-2 mx-2 rounded-lg shadow-md ${iconBg}`}>
                    <Icon className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-200">{title}</h2>
                {badge && (
                    <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-full">
                        {badge}
                    </span>
                )}
            </div>
            <div
                className={`p-1.5 rounded-md transition-colors ${
                    expandedSections[section]
                        ? "bg-indigo-100 dark:bg-indigo-900/30"
                        : "bg-slate-200 dark:bg-slate-700"
                }`}
            >
                {expandedSections[section] ? (
                    <ChevronUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                ) : (
                    <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                )}
            </div>
        </button>
    );
};

const InputField = ({ label, required, children, className = '' }) => (
    <div className={className}>
        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        {children}
    </div>
);

const MBL = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const urlBookingId = searchParams.get("bookingId");
    const { bookingId: stateBookingId, FILENUMBER } = location.state || {};
    const bookingId = stateBookingId || urlBookingId;
    const { countryList, documentTypePurchaseList, documentTypeSalesList, incoTermsList, quantityTypeList, salesPersonList, uomTypeList,
        commodityGroupList, containerTypeList, operatorList, moveTypeList, currency_list, charges_basis_list, fileManagerList } = useSelector((state) => state.common);

    const [formData, setFormData] = useState();
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
    const [rows, setRows] = useState([]);
    const [dateErrors, setDateErrors] = useState({});
    const [errors, setErrors] = useState({});
    const [currentStep, setCurrentStep] = useState(0);

    const fileInfoRef = useRef(null);
    const partiesRef = useRef(null);
    const routingRef = useRef(null);
    const particularsRef = useRef(null);
    const chargesRef = useRef(null);
    const paymentPrintRef = useRef(null);

    const stepSectionMap = useMemo(
        () => ["fileInfo", "parties", "routing", "particulars", "charges", "paymentPrint"],
        []
    );
    const sectionRefs = useMemo(
        () => ({
            fileInfo: fileInfoRef,
            parties: partiesRef,
            routing: routingRef,
            particulars: particularsRef,
            charges: chargesRef,
            paymentPrint: paymentPrintRef,
        }),
        []
    );
    const steps = useMemo(
        () => ["File Info", "Parties", "Routing", "Cargo", "Charges", "Payment & Print"],
        []
    );

    const initialMBLdetail = {
        mblManager: '',
        fileNumber: '3605399091',
        MBL_NUMBER: 'MAEU12345678',
        CARRIER_BOOKING_NO: 'BK2024001234',
        exportRef: '',
        forwardingAgentRef: '',
        shipper: '',
        shipperAddress: '',
        consignee: '',
        consigneeAddress: '',
        notifyParty: '',
        notifyAddress: '',
        vessel: '',
        voyage: '',
        portOfLoading: '',
        portOfDischarge: '',
        placeOfReceipt: '',
        placeOfDelivery: '',
        finalDestination: '',
        freightPayable: 'Prepaid',
        numberOfOriginals: '3',
        placeOfIssue: '',
        dateOfIssue: '',
        containers: [
            { containerNo: '', sealNo: '', size: '40STD', quantity: 1, description: '', weight: '', measurement: '' }
        ]
    }
    const initialrow = {
        ID: 0,
        MBL_ID: 0,
        LINE_SEQ_NO: 0,
        CONTAINER_NUMBER: "",
        SEAL_NUMBER: "",
        MARKS_NO: "",
        CONT_SIZE: "",
        CONT_TYPE: "",
        CONT_TYPE_CODE: "",
        CONT_TYPE_NAME: "",
        TARE_WEIGHT: "",
        NO_PACKAGE: 1,
        PACKAGE_CODE: "",
        SCHEDULE_B: "",
        PACKAGE_DESCRIPTION: "",
        DESCRIPTIONS: "",
        KGS: 0,
        LBS: 0,
        CBM: 0,
        CFT: 0,
        RIDER_PAGE_NO: 0,
        IS_DELETED: false,
        HAZ_CODE: "",
        MANUFACTURER: "",
        COUNTRY: "",
        IT_NUMBER: "",
        CNC_PRS_NUMBER: "",
        PICK_UP_NUMBER: "",
        LAST_DATE: new Date().toISOString(),
        SENT_TO_RAIL_INC: false,
        SENT_DATE_TO_RAIL_INC: null,
        IS_APPLIED_ON_HBL: false,
        NET_KGS: 0,
        NET_LBS: 0,
        IS_MERGED_PROCESS_ID: 0,
        IS_SPLITTED_PROCESS_ID: 0,
        CONTAINER_PARENT_ID: 0,
        VIN_NUMBER: "",
        TITLE_NUMBER: "",
        CARGO_STATE: "",
        IMPORT_FROM_CARGO_UNIQUE_ID: 0,
        CREATED_DATE: new Date().toISOString()
    };

    useEffect(() => {
        if (bookingId != 0 && bookingId != null && bookingId != undefined) {
            _getMBLdetail();
        } else {
            _setNewBooking();
        }
    }, [bookingId])

    const _getMBLdetail = async () => {
        try {
            dispatch(callLoader(1));
            const resultData = await dispatch(GetMBLDetails(bookingId));
            console.log('data is comeing', resultData);
            setFormData(resultData);
            setFormData(prev => ({
                ...prev,
                MODIFIED_BY: `${authUser?.firstName ?? ''} ${authUser?.lastName ?? ''}`.trim(),
                companyId: authUser?.companyId
            }));

            setRows(
                resultData?.MasterBillLoadingDescriptions?.length > 0
                    ? resultData?.MasterBillLoadingDescriptions
                    : [initialrow]
            );
        } catch (error) {
            console.error("Error fetching booking details:", error);
            toast.error("Failed to load MBL details");
        } finally {
            dispatch(callLoader(0));
        }
    }

    const _setNewBooking = async () => {
        setFormData({
            ...initialMBLdetail,
            SHIPMENT_TYPE: 'FCL',
            CREATED_BY: authUser?.firstName + ' ' + authUser?.lastName,
            companyId: authUser?.companyId
        });
    }

    const [expandedSections, setExpandedSections] = useState({
        file1: true,
        file2: true,
        shipper: true,
        consignee: true,
        notify: true,
        alsonotify: true,
        routing: true,
        containers: true,
        additional: true,
        charges: true,
        payment: true,
        release: true,
        inttra1: true,
        inttra2: true,
        remarks: true,
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const scrollToSection = (index) => {
        const key = stepSectionMap[index];
        const ref = sectionRefs[key];
        if (ref?.current) {
            ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
            setExpandedSections((prev) => {
                const next = { ...prev };
                if (key === "fileInfo") {
                    next.file1 = true;
                    next.file2 = true;
                }
                if (key === "parties") {
                    next.shipper = true;
                    next.consignee = true;
                    next.notify = true;
                    next.alsonotify = true;
                }
                if (key === "routing") next.routing = true;
                if (key === "particulars") next.containers = true;
                if (key === "charges") {
                    next.additional = true;
                    next.charges = true;
                }
                if (key === "paymentPrint") {
                    next.payment = true;
                    next.release = true;
                    next.inttra1 = true;
                    next.inttra2 = true;
                    next.remarks = true;
                }
                return next;
            });
        }
    };

    useEffect(() => {
        const observers = [];
        stepSectionMap.forEach((key, index) => {
            const ref = sectionRefs[key];
            if (!ref?.current) return;
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) setCurrentStep(index);
                },
                { root: null, rootMargin: "-30% 0px -60% 0px", threshold: 0 }
            );
            observer.observe(ref.current);
            observers.push(observer);
        });
        return () => observers.forEach((o) => o.disconnect());
    }, [formData, stepSectionMap, sectionRefs]);

    const isSectionFilled = useCallback(
        (key) => {
            if (!formData) return false;
            switch (key) {
                case "fileInfo":
                    return true;
                case "parties":
                    return !!(formData?.SHIPPER && String(formData.SHIPPER).trim());
                case "routing":
                    return !!(formData?.PORT_OF_LOADING && String(formData.PORT_OF_LOADING).trim());
                case "particulars":
                    return Array.isArray(rows) && rows.some((r) => r && r.IS_DELETED === false);
                case "charges":
                    return true;
                case "paymentPrint":
                    return true;
                default:
                    return false;
            }
        },
        [formData, rows]
    );

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const _setAddress = async (type, id) => {
        const address = await getAddressAPI(id);
        setFormData(prev => ({ ...prev, [`${type}`]: address }));
    };

    const ADDRESS_FIELD_MAP = {
        shipper: {
            id: 'SHIPPER_ID',
            name: 'SHIPPER',
            address: 'SHIPPER_ADDRESS',
            contact: 'SHIPPER_CONTACT_NAME',
            phone: 'SHIPPER_PHONE',
            email: 'SHIPPER_EMAIL',
        },
        consignee: {
            id: 'CONSIGNEE_ID',
            name: 'CONSIGNEE',
            address: 'CONSIGNEE_ADDRESS',
            contact: 'CONSIGNEE_CONTACT_NAME',
            phone: 'CONSIGNEE_PHONE',
            email: 'CONSIGNEE_EMAIL',
        },
        notify: {
            id: 'NOTIFY_ID',
            name: 'NOTIFY',
            address: 'NOTIFY_ADDRESS',
            contact: 'NOTIFY_CONTACT_NAME',
            phone: 'NOTIFY_PHONE',
            email: 'NOTIFY_EMAIL',
        },
        forwardingAgent: {
            id: 'FORWARDING_AGENT_ID',
            name: 'FORWARDING_AGENT',
            address: 'FORWARDING_AGENT_ADDRESS',
            contact: 'FORWARDER_CONTACT_NAME',
            phone: 'FORWARDER_PHONE',
            email: 'FORWARDER_EMAIL',
        },
        delivery: {
            id: 'DELIVERY_ID',
            name: 'DELIVERY_AGENT',
            address: 'DELIVERY_ADDRESS',
            contact: 'DESTINATION_CONTACT_NAME',
            phone: 'DESTINATION_PHONE',
            email: 'DESTINATION_EMAIL',
        },
        alsoNotify: {
            id: 'ALSO_NOTIFY_ID',
            name: 'ALSO_NOTIFY',
            address: 'ALSO_NOTIFY_ADDRESS',
            contact: 'ALSO_NOTIFY_CONTACT_NAME',
            phone: 'ALSO_NOTIFY_PHONE',
            email: 'ALSO_NOTIFY_EMAIL',
        },
        billTo: {
            id: 'BILL_TO_ID',
            name: 'BILL_TO_PARTY',
            address: 'BILL_TO_PARTY_ADDRESS',
            contact: 'BILL_TO_PARTY_CONTACT_NAME',
            phone: 'BILL_TO_PARTY_PHONE',
            email: 'BILL_TO_PARTY_EMAIL',
        },
    };

    const _getAddresswithfulldetail = async (type, id) => {
        try {
            const res = await getAddresswithfulldetail(id);
            const data = res?.data?.[0];
            if (!data || !ADDRESS_FIELD_MAP[type]) return;
            const map = ADDRESS_FIELD_MAP[type];
            setFormData((prev) => ({
                ...prev,
                [map.address]: data.completeAddress || '',
                [map.contact]: data.contactname || '',
                [map.phone]: data.addressphno || '',
                [map.email]: data.email || '',
            }));
        } catch (error) {
            console.error('Error fetching address details:', error);
        }
    };

    const handleSameAsCONSIGNEEChange = (e) => {
        const checked = e.target.checked;
        setFormData((prev) => ({
            ...prev,
            NOTIFY_ID: checked ? prev.CONSIGNEE_ID : '',
            NOTIFY: checked ? prev.CONSIGNEE : '',
            NOTIFY_ADDRESS: checked ? prev.CONSIGNEE_ADDRESS : '',
            NOTIFY_CONTACT_NAME: checked ? prev.CONSIGNEE_CONTACT_NAME : '',
            NOTIFY_PHONE: checked ? prev.CONSIGNEE_PHONE : '',
            NOTIFY_EMAIL: checked ? prev.CONSIGNEE_EMAIL : '',
        }));
    };

    useEffect(() => {
        calculateTotals();
    }, [rows]);

    const calculateTotals = () => {
        let totals = {
            containers: 0,
            packages: 0,
            netKgs: 0,
            netLbs: 0,
            kgs: 0,
            lbs: 0,
            cbm: 0,
            cft: 0
        };

        rows
            .filter(r => r.IS_DELETED === false)
            .forEach(r => {
                totals.containers += 1;
                totals.packages += Number(r.NO_PACKAGE) || 0;
                totals.netKgs += Number(r.NET_KGS) || 0;
                totals.netLbs += Number(r.NET_LBS) || 0;
                totals.kgs += Number(r.KGS) || 0;
                totals.lbs += Number(r.LBS) || 0;
                totals.cbm += Number(r.CBM) || 0;
                totals.cft += Number(r.CFT) || 0;
            });

        setFormData(prev => ({
            ...prev,
            TOTAL_CONTAINER: totals.containers,
            TOTAL_PACKAGE: totals.packages,
            TOTAL_NET_KGS: Number(totals.netKgs).toFixed(2),
            TOTAL_NET_LBS: Number(totals.netLbs).toFixed(2),
            TOTAL_KGS: Number(totals.kgs).toFixed(2),
            TOTAL_LBS: Number(totals.lbs).toFixed(2),
            TOTAL_CBM: Number(totals.cbm).toFixed(2),
            TOTAL_CFT: Number(totals.cft).toFixed(2)
        }));
    };

    const _handleInputChange = (index, field, value) => {
        const updated = [...rows];
        updated[index][field] = value;
        setRows(updated);
    };

    const KG_TO_LBS = 2.20462;
    const LBS_TO_KG = 0.453592;
    const CBM_TO_CFT = 35.3147;
    const CFT_TO_CBM = 0.0283168;

    const handleWeightChange = (rowIndex, field, value) => {
        const num = parseFloat(value);
        setRows(prevRows => {
            const updatedRows = [...prevRows];
            const row = { ...updatedRows[rowIndex], [field]: value };
            if (!isNaN(num)) {
                switch (field) {
                    case 'NET_KGS':
                        row.NET_LBS = (num * KG_TO_LBS).toFixed(2);
                        break;
                    case 'NET_LBS':
                        row.NET_KGS = (num * LBS_TO_KG).toFixed(2);
                        break;
                    case 'KGS':
                        row.LBS = (num * KG_TO_LBS).toFixed(2);
                        break;
                    case 'LBS':
                        row.KGS = (num * LBS_TO_KG).toFixed(2);
                        break;
                    case 'CBM':
                        row.CFT = (num * CBM_TO_CFT).toFixed(2);
                        break;
                    case 'CFT':
                        row.CBM = (num * CFT_TO_CBM).toFixed(2);
                        break;
                    default:
                        break;
                }
            }
            updatedRows[rowIndex] = row;
            return updatedRows;
        });
    };

    const addRow = () => {
        setRows(prev => {
            const nextId = prev.length + 1;
            return [
                ...prev,
                {
                    ...initialrow,
                    ID: nextId,
                    LINE_SEQ_NO: nextId,
                    CONTAINER_NUMBER: "",
                    IS_DELETED: false,
                    CREATED_DATE: new Date().toISOString()
                }
            ];
        });
    };

    const handleDateChange = (name, value) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value ? value : null,
        }));
    };

    const handleDateError = (fieldName, hasError) => {
        setDateErrors(prev => ({
            ...prev,
            [fieldName]: hasError
        }));
    };

    const handleSave = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        try {
            dispatch(callLoader(1));
            const payload = {
                ...formData,
                MasterBillLoadingDescriptions: rows
            }
            console.log('Payload:', payload);
            const validationErrors = validateForm({
                data: formData,
                items: [],
                rules: ValidationMBLfrom,
            });
            setErrors(validationErrors);
            if (Object.keys(validationErrors).length > 0) {
                return;
            }
            if (Object.values(dateErrors).some(Boolean)) {
                toast.error("Please correct the date fields");
                return;
            }
            let response = await updateMBLDetails(payload);
            if (response?.statuscode === 200 || response?.statuscode === 201) {
                toast.success("MBL details saved successfully");
                return { success: true, data: response.data };
            }
            return { success: false, status: response?.status };
        } catch (error) {
            toast.error(
                error?.response?.data?.title ||
                error?.message ||
                "Something went wrong"
            );
            return {
                success: false,
                status: error?.response?.status,
                errors: error?.response?.data?.errors,
            };
        } finally {
            dispatch(callLoader(0));
        }
    };

    const _getPortCode = (strdata) => {
        if (!strdata) return "";
        const match = strdata.match(/\[(.*?)\]/);
        const code = match ? match[1] : "";
        console.log(code);
        return code;
    }

    const inputClass =
        "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all";
    const selectClass =
        "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all appearance-none cursor-pointer";
    const readonlyinputClass =
        "w-full px-3 py-2 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-slate-300 placeholder-slate-400 cursor-not-allowed";

    return (
        <>
            <div className="page-header-sticky top-0 z-50">
                <PageHeader
                    icon={<Anchor className="w-5 h-5 text-white" />}
                    title="Master Bill of Lading"
                    subtitle={
                        FILENUMBER ? (
                            <>
                                {"File No. "}
                                <span className="text-slate-700 font-semibold dark:text-slate-300">{FILENUMBER}</span>
                                {" · "}
                                <span className="font-mono">{formData?.MBL_NUMBER || "NEW"}</span>
                            </>
                        ) : (
                            "Create and manage BL documentation"
                        )
                    }
                    actions={[
                        { label: "Cancel", icon: <X />, onClick: () => navigate("/booking"), variant: "ghost" },
                        {
                            label: "Print",
                            icon: <Printer />,
                            onClick: (e) => {
                                e.stopPropagation();
                                navigate('/mblPrint', { state: { bookingId: bookingId, FILENUMBER: FILENUMBER } });
                            },
                            variant: "tertiary",
                        },
                        "|",
                         {
                        label:"HBL",
                        onClick:(e) =>{
                            e.stopPropagation();
                                navigate("/hbl", {
                                    state: {
                                        bookingId: bookingId,
                                        FILENUMBER: FILENUMBER,
                                    },
                                })
                            }
,
                      },
                        // {
                        //     label: "Load BL",
                        //     icon: <Upload />,
                        //     onClick: (e) => e.stopPropagation(),
                        //     variant: "tertiary",
                        // },
                    ]}
                    onSubmit={handleSave}
                    submitLabel="Submit MBL"
                />
                <div style={{ height: "var(--page-header-height)" }} />
            </div>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="min-h-screen bg-slate-50 dark:bg-slate-900 sticky "
            >
                <div className="p-4 space-y-3 pb-16">
                    {/* Progress Bar */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 px-4 py-3 overflow-x-auto">
                      <div className="flex items-center w-full justify-between gap-0">
    {steps.map((step, index) => {
        const sectionKey = stepSectionMap[index];
        const filled = isSectionFilled(sectionKey);
        const done = filled && index !== currentStep;
        const active = index === currentStep;
        return (
            <React.Fragment key={step}>
                <div
                    className={[
                        "flex items-center gap-2 h-8 pl-2 pr-3 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 select-none",
                        done && "text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20",
                        active && "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
                        !done && !active && "text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800",
                    ]
                        .filter(Boolean)
                        .join(" ")}
                    onClick={() => scrollToSection(index)}
                >
                    <span
                        className={[
                            "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0",
                            done && "bg-green-500 text-white",
                            active && "bg-blue-600 text-white",
                            !done && !active && "bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400",
                        ]
                            .filter(Boolean)
                            .join(" ")}
                    >
                        {done ? <Check className="w-3 h-3" /> : index + 1}
                    </span>
                    <span className="hidden sm:inline">{step}</span>
                </div>

                {/* Render connector AFTER every step including last */}
                {index < steps.length - 1 && (
                    <div
                        className={[
                            "flex-1 h-0.5 min-w-[8px] mx-1 rounded-full transition-colors duration-300",
                            done ? "bg-green-400 dark:bg-green-600" : "bg-slate-200 dark:bg-slate-700",
                        ].join(" ")}
                    />
                )}
            </React.Fragment>
        );
    })}
</div>
                    </div>

                    {/* File Information (file1 + file2) */}
                    <div ref={fileInfoRef} className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                        <SectionCard>
                            <SectionHeader
                                icon={Info}
                                title="File Information"
                                color="purple"
                                section="file1"
                                expandedSections={expandedSections}
                                toggleSection={toggleSection}
                            />
                            {expandedSections.file1 && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    className="p-5"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                        <InputField label="BL Manager" required>
                                            <select name="FILE_MANAGER" value={formData?.FILE_MANAGER} onChange={(e) =>
                                                handleInputChange({
                                                    target: {
                                                        name: "FILE_MANAGER",
                                                        value: e.target.value,
                                                    },
                                                })
                                            } className={selectClass}>
                                                <option value="">SELECT Manager</option>
                                                {
                                                    fileManagerList.map((item, index) => (
                                                        <option key={index} value={item.id}>{item.filemanager}</option>
                                                    ))
                                                }
                                            </select>
                                        </InputField>
                                        <InputField label="Master BL Number" required>
                                            <input type="text" name="MBL_NUMBER" readOnly value={formData?.MBL_NUMBER} onChange={handleInputChange} className={readonlyinputClass} />
                                        </InputField>
                                        <InputField label="Forwarder Ref. No">
                                            <input type="text" name="FORWARDER_REF_NO" value={formData?.FORWARDER_REF_NO} onChange={handleInputChange} className={inputClass} />
                                        </InputField>
                                        <InputField label="Customer Ref. #">
                                            <input type="text" name="CUSTOMER_PO_NO" value={formData?.CUSTOMER_PO_NO} onChange={handleInputChange} className={inputClass} />
                                        </InputField>
                                        <InputField label="ITN Number:">
                                            <input type="text" name="ITN_NUMBER" value={formData?.ITN_NUMBER} onChange={handleInputChange} className={inputClass} />
                                        </InputField>
                                        <InputField label="Lock BL:">
                                            <input type="checkbox" name="lockbl" checked={formData?.forwardingAgentRef}
                                                onChange={handleInputChange}
                                                className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500" />
                                        </InputField>
                                    </div>
                                </motion.div>
                            )}
                        </SectionCard>

                        <SectionCard>
                            <SectionHeader
                                icon={Info}
                                title="Carrier & References"
                                color="pink"
                                section="file2"
                                expandedSections={expandedSections}
                                toggleSection={toggleSection}
                            />
                            {expandedSections.file2 && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    className="p-5"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                        <InputField label="Carrier Booking No.">
                                            <input type="text" name="CARRIER_BOOKING_NO" value={formData?.CARRIER_BOOKING_NO} onChange={handleInputChange} className={inputClass} />
                                        </InputField>
                                        <InputField label="Carrier BL Number:">
                                            <input type="text" name="CARRIER_MBL_NO" value={formData?.CARRIER_MBL_NO} onChange={handleInputChange} className={inputClass} />
                                        </InputField>
                                        <div>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                                                    Export Ref. Number:
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                        type="checkbox"
                                                        name="hideonprint"
                                                    />
                                                    <label className="text-xs text-slate-600 dark:text-slate-400">
                                                        Hide on Print
                                                    </label>
                                                </div>
                                            </div>
                                            <textarea
                                                rows="2"
                                                onChange={handleInputChange}
                                                name="EXPORT_REF_NUMBER"
                                                value={formData?.EXPORT_REF_NUMBER}
                                                className={inputClass}
                                            ></textarea>
                                        </div>
                                        <InputField label="MRN Number:">
                                            <input type="text" name="MRN_NUMBER" value={formData?.MRN_NUMBER} onChange={handleInputChange} className={inputClass} />
                                        </InputField>
                                        <InputField label="Enable SI EDI Data Truncation:">
                                            <input type="checkbox" name="IS_SI_EDI_DATA_TRUNCATE" checked={formData?.IS_SI_EDI_DATA_TRUNCATE}
                                                onChange={handleInputChange} className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500" />
                                        </InputField>
                                    </div>
                                </motion.div>
                            )}
                        </SectionCard>
                    </div>

                    {/* Parties: Shipper, Forwarding, Consignee, Destination, Notify, Also Notify */}
                    <div ref={partiesRef} className="space-y-3">
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            <SectionCard>
                                <SectionHeader
                                    icon={User}
                                    title="Shipper Information"
                                    color="blue"
                                    section="shipper"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.shipper && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="p-5 space-y-4"
                                    >
                                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                                            <InputField label="Shipper" required>
                                                <SearchBar
                                                    searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                                    selectReacord={(d) => {
                                                        handleInputChange({ target: { name: 'SHIPPER', value: d?.[1] } });
                                                        handleInputChange({ target: { name: 'SHIPPER_ID', value: d?.[0] } });
                                                        _getAddresswithfulldetail('shipper', d?.[0]);
                                                    }}
                                                    onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'SHIPPER', value: '' } }); handleInputChange({ target: { name: 'SHIPPER_ID', value: 0 } }); } }}
                                                    value={formData?.SHIPPER || ''}
                                                />
                                            </InputField>
                                            <div className="mt-3">
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5">Shipper Address</label>
                                                <textarea
                                                    name="SHIPPER_ADDRESS"
                                                    value={formData?.SHIPPER_ADDRESS}
                                                    onChange={handleInputChange}
                                                    rows="4"
                                                    placeholder="Enter shipper complete address..."
                                                    className={inputClass}
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                <InputField label="Contact Name:">
                                                    <input type="text" name="SHIPPER_CONTACT_NAME" value={formData?.SHIPPER_CONTACT_NAME} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Telephone:">
                                                    <input type="text" name="SHIPPER_PHONE" value={formData?.SHIPPER_PHONE} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Email:">
                                                    <input type="text" name="SHIPPER_EMAIL" value={formData?.SHIPPER_EMAIL} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                                <InputField label="Special Verbiage:">
                                                    <select name="SPECIAL_VERBIAGE_TYPE" value={formData?.SPECIAL_VERBIAGE_TYPE} onChange={(e) =>
                                                        handleInputChange({
                                                            target: {
                                                                name: "SPECIAL_VERBIAGE_TYPE",
                                                                value: e.target.value,
                                                            },
                                                        })
                                                    } className={selectClass}>
                                                        <option value="">Select Verbiage type </option>
                                                        <option value="NVOCC FOR">NVOCC FOR</option>
                                                        <option value="AS AGENT FOR">AS AGENT FOR</option>
                                                        <option value="IN CARE OF">IN CARE OF</option>
                                                        <option value="ON BEHALF OF">ON BEHALF OF</option>
                                                    </select>
                                                </InputField>
                                                <div className="mt-3">
                                                    <textarea
                                                        name="SPECIAL_VERBIAGE_ADDRESS"
                                                        value={formData?.SPECIAL_VERBIAGE_ADDRESS}
                                                        onChange={handleInputChange}
                                                        rows="2"
                                                        placeholder="Enter special verbiage complete address..."
                                                        className={inputClass}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>

                            <SectionCard>
                                <SectionHeader
                                    icon={User}
                                    title="Forwarding Instructions"
                                    color="green"
                                    section="shipper"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.shipper && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="p-5 space-y-4"
                                    >
                                        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800">
                                            <InputField label="Forwarding Agent:">
                                                <SearchBar
                                                    searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                                    selectReacord={(d) => {
                                                        handleInputChange({ target: { name: 'FORWARDING_AGENT', value: d?.[1] } });
                                                        handleInputChange({ target: { name: 'FORWARDING_AGENT_ID', value: d?.[0] } });
                                                        _getAddresswithfulldetail('forwardingAgent', d?.[0]);
                                                    }}
                                                    onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'FORWARDING_AGENT', value: '' } }); handleInputChange({ target: { name: 'FORWARDING_AGENT_ID', value: 0 } }); } }}
                                                    value={formData?.FORWARDING_AGENT || ''}
                                                />
                                            </InputField>
                                            <div className="mt-3">
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400"> Forwarding Address: </label>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                            type="checkbox"
                                                            name="FORWARDING_AGENT_HIDE_ON_MBL"
                                                            checked={formData?.FORWARDING_AGENT_HIDE_ON_MBL}
                                                            onChange={handleInputChange}
                                                        />
                                                        <label className="text-xs text-slate-600 dark:text-slate-400">
                                                            Hide on BL
                                                        </label>
                                                    </div>
                                                </div>
                                                <textarea
                                                    rows="4"
                                                    onChange={handleInputChange}
                                                    name="FORWARDING_AGENT_ADDRESS"
                                                    value={formData?.FORWARDING_AGENT_ADDRESS}
                                                    placeholder="Enter complete address..."
                                                    className={inputClass}
                                                ></textarea>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                                <InputField label="Contact Name:">
                                                    <input type="text" name="FORWARDER_CONTACT_NAME" value={formData?.FORWARDER_CONTACT_NAME} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Telephone:">
                                                    <input type="text" name="FORWARDER_PHONE" value={formData?.FORWARDER_PHONE} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Email:">
                                                    <input type="text" name="FORWARDER_EMAIL" value={formData?.FORWARDER_EMAIL} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <div>
                                                    <div className="flex items-center justify-between mb-1.5">
                                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">FMC Licence Number: </label>
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                                type="checkbox"
                                                                name="IS_HIDE_FMC_MBL"
                                                                checked={formData?.IS_HIDE_FMC_MBL} onChange={handleInputChange}
                                                            />
                                                            <label className="text-xs text-slate-600 dark:text-slate-400">
                                                                Hide on BL
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <input type="text" name="FMC_NO" value={formData?.FMC_NO} onChange={handleInputChange} className={inputClass} />
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            <SectionCard>
                                <SectionHeader
                                    icon={User}
                                    title="Consignee Information"
                                    color="cyan"
                                    section="consignee"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.consignee && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="p-5 space-y-4"
                                    >
                                        <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800">
                                            <InputField label="Consignee" required>
                                                <SearchBar
                                                    searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                                    selectReacord={(d) => {
                                                        handleInputChange({ target: { name: 'CONSIGNEE', value: d?.[1] } });
                                                        handleInputChange({ target: { name: 'CONSIGNEE_ID', value: d?.[0] } });
                                                        _getAddresswithfulldetail('consignee', d?.[0]);
                                                    }}
                                                    onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'CONSIGNEE', value: '' } }); handleInputChange({ target: { name: 'CONSIGNEE_ID', value: 0 } }); } }}
                                                    value={formData?.CONSIGNEE || ''}
                                                />
                                            </InputField>
                                            <div className="mt-3">
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5">Consignee Address</label>
                                                <textarea
                                                    name="CONSIGNEE_ADDRESS"
                                                    value={formData?.CONSIGNEE_ADDRESS}
                                                    onChange={handleInputChange}
                                                    rows="4"
                                                    placeholder="Enter consignee complete address..."
                                                    className={inputClass}
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                                <InputField label="Contact Name:">
                                                    <input type="text" name="CONSIGNEE_CONTACT_NAME" value={formData?.CONSIGNEE_CONTACT_NAME} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Telephone:">
                                                    <input type="text" name="CONSIGNEE_PHONE" value={formData?.CONSIGNEE_PHONE} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Email:">
                                                    <input type="text" name="CONSIGNEE_EMAIL" value={formData?.CONSIGNEE_EMAIL} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>

                            <SectionCard>
                                <SectionHeader
                                    icon={User}
                                    title="Destination Information"
                                    color="slate"
                                    section="consignee"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.consignee && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="p-5 space-y-4"
                                    >
                                        <div className="p-4 bg-stone-50 dark:bg-stone-900/20 rounded-xl border border-stone-200 dark:border-stone-800">
                                            <InputField label="Destination">
                                                <SearchBar
                                                    searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                                    selectReacord={(d) => {
                                                        handleInputChange({ target: { name: 'DELIVERY_AGENT', value: d?.[1] } });
                                                        handleInputChange({ target: { name: 'DELIVERY_ID', value: d?.[0] } });
                                                        _getAddresswithfulldetail('delivery', d?.[0]);
                                                    }}
                                                    onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'DELIVERY_AGENT', value: '' } }); handleInputChange({ target: { name: 'DELIVERY_ID', value: 0 } }); } }}
                                                    value={formData?.DELIVERY_AGENT || ''}
                                                />
                                            </InputField>
                                            <div className="mt-3">
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5">Destination Address</label>
                                                <textarea
                                                    name="DELIVERY_ADDRESS"
                                                    value={formData?.DELIVERY_ADDRESS}
                                                    onChange={handleInputChange}
                                                    rows="4"
                                                    placeholder="Enter complete address..."
                                                    className={inputClass}
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                                <InputField label="Contact Name:">
                                                    <input type="text" name="DESTINATION_CONTACT_NAME" value={formData?.DESTINATION_CONTACT_NAME} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Telephone:">
                                                    <input type="text" name="DESTINATION_PHONE" value={formData?.DESTINATION_PHONE} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Email:">
                                                    <input type="text" name="DESTINATION_EMAIL" value={formData?.DESTINATION_EMAIL} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            <SectionCard>
                                <SectionHeader
                                    icon={User}
                                    title="Notify Party Information"
                                    color="orange"
                                    section="notify"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.notify && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="p-5"
                                    >
                                        <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                    type="checkbox"
                                                    name="NOTIFY_SAME_AS_CONSIGNEE"
                                                    value={formData?.NOTIFY_SAME_AS_CONSIGNEE} onChange={handleSameAsCONSIGNEEChange}
                                                />
                                                <label className="text-sm text-slate-600 dark:text-slate-400">
                                                    :Notify Same as Consignee
                                                </label>
                                            </div>
                                            <InputField label="Notify Party">
                                                <SearchBar
                                                    searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                                    selectReacord={(d) => {
                                                        handleInputChange({ target: { name: 'NOTIFY', value: d?.[1] } });
                                                        handleInputChange({ target: { name: 'NOTIFY_ID', value: d?.[0] } });
                                                        _getAddresswithfulldetail('notify', d?.[0]);
                                                    }}
                                                    onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'NOTIFY', value: '' } }); handleInputChange({ target: { name: 'NOTIFY_ID', value: 0 } }); } }}
                                                    value={formData?.NOTIFY || ''}
                                                />
                                            </InputField>
                                            <div className="mt-3">
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5">Notify Address</label>
                                                <textarea name="NOTIFY_ADDRESS" value={formData?.NOTIFY_ADDRESS} onChange={handleInputChange} rows="3"
                                                    placeholder="Enter notify party address..." className={inputClass} />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                                <InputField label="Contact Name:">
                                                    <input type="text" name="NOTIFY_CONTACT_NAME" value={formData?.NOTIFY_CONTACT_NAME} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Telephone:">
                                                    <input type="text" name="NOTIFY_PHONE" value={formData?.NOTIFY_PHONE} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Email:">
                                                    <input type="text" name="NOTIFY_EMAIL" value={formData?.NOTIFY_EMAIL} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>

                            <SectionCard>
                                <SectionHeader
                                    icon={User}
                                    title="Also Notify Party Information"
                                    color="violet"
                                    section="alsonotify"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.alsonotify && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="p-5"
                                    >
                                        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
                                            <InputField label="Also Notify Party">
                                                <SearchBar
                                                    searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                                    selectReacord={(d) => {
                                                        handleInputChange({ target: { name: 'ALSO_NOTIFY', value: d?.[1] } });
                                                        handleInputChange({ target: { name: 'ALSO_NOTIFY_ID', value: d?.[0] } });
                                                        _getAddresswithfulldetail('alsoNotify', d?.[0]);
                                                    }}
                                                    onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'ALSO_NOTIFY', value: '' } }); handleInputChange({ target: { name: 'ALSO_NOTIFY_ID', value: 0 } }); } }}
                                                    value={formData?.ALSO_NOTIFY || ''}
                                                />
                                            </InputField>
                                            <div className="mt-3">
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5">Also Notify Address</label>
                                                <textarea name="ALSO_NOTIFY_ADDRESS" value={formData?.ALSO_NOTIFY_ADDRESS} onChange={handleInputChange} rows="3" placeholder="Enter also notify party address..." className={inputClass} />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                                <InputField label="Contact Name:">
                                                    <input type="text" name="ALSO_NOTIFY_CONTACT_NAME" value={formData?.ALSO_NOTIFY_CONTACT_NAME} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Telephone:">
                                                    <input type="text" name="ALSO_NOTIFY_PHONE" value={formData?.ALSO_NOTIFY_PHONE} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Email:">
                                                    <input type="text" name="ALSO_NOTIFY_EMAIL" value={formData?.ALSO_NOTIFY_EMAIL} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>
                        </div>
                    </div>

                    {/* Routing */}
                    <div ref={routingRef} className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        <SectionCard>
                            <SectionHeader
                                icon={Route}
                                title="Routing Information"
                                color="cyan"
                                section="routing"
                                expandedSections={expandedSections}
                                toggleSection={toggleSection}
                            />
                            {expandedSections.routing && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    className="p-5"
                                >
                                    <div className="mb-6 px-4 sm:px-5 py-4 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
    {/* Row 1 — labels */}
    <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
        <p className="text-[10px] font-semibold tracking-widest text-slate-400 dark:text-slate-500 uppercase">
            Origin
        </p>
        <div />
        <p className="text-[10px] font-semibold tracking-widest text-slate-400 dark:text-slate-500 uppercase text-right">
            Destination
        </p>
    </div>

    {/* Row 2 — flags + connecting line + ship */}
    <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 mt-1.5">
        {/* Origin badge */}
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-md shadow-blue-400/30 ring-2 ring-white dark:ring-slate-900">
            {formData?.PORT_OF_LOADING_CODE ? (
                <InlineCountryFlag code={formData?.PORT_OF_LOADING_CODE} className="w-5 h-5" />
            ) : (
                <MapPin className="w-5 h-5 text-white" />
            )}
        </div>

        {/* Line + centered ship */}
        <div className="relative h-11 flex items-center min-w-0">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
            <div className="flex-1 h-[3px] rounded-full bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400" />
            <div className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 flex items-center justify-center shadow-md shadow-cyan-300/20">
                <Ship className="w-4 h-4 text-cyan-500" />
            </div>
        </div>

        {/* Destination badge */}
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-md shadow-teal-400/30 ring-2 ring-white dark:ring-slate-900">
            {formData?.PORT_OF_DISCHARGE_CODE ? (
                <InlineCountryFlag code={formData?.PORT_OF_DISCHARGE_CODE} className="w-5 h-5" />
            ) : (
                <MapPin className="w-5 h-5 text-white" />
            )}
        </div>
    </div>

    {/* Row 3 — port names */}
    <div className="grid grid-cols-[auto_1fr_auto] items-start gap-3 mt-2">
        <p className="max-w-[16ch] text-xs sm:text-sm font-bold text-slate-900 dark:text-white truncate">
            {formData?.PORT_OF_LOADING || "Port of Loading"}
        </p>
        <div />
        <p className="max-w-[16ch] text-xs sm:text-sm font-bold text-slate-900 dark:text-white truncate text-right">
            {formData?.PORT_OF_DISCHARGE || "Port of Discharge"}
        </p>
    </div>

    {/* Optional move-type pill */}
    {formData?.MOVE_TYPE ? (
        <div className="flex justify-center mt-3">
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-[11px] font-medium">
                <Ship className="w-3 h-3" />
                {formData.MOVE_TYPE}
            </span>
        </div>
    ) : null}
</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                        <InputField label="Place of Receipt">
                                            <SearchBar
                                                searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                                selectReacord={(d) => {
                                                    handleInputChange({ target: { name: 'PLACE_OF_RECEIPT', value: d?.[1] } })
                                                    handleInputChange({ target: { name: 'PLACE_OF_RECEIPT_CODE', value: _getPortCode(d?.[1]) } })
                                                }}
                                                onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'PLACE_OF_RECEIPT', value: '' } }); handleInputChange({ target: { name: 'PLACE_OF_RECEIPT_CODE', value: '' } }); } }}
                                                value={formData?.PLACE_OF_RECEIPT}
                                            />
                                        </InputField>
                                        <InputField label="Show on BL:(* Use customized Loc. for AFR)">
                                            <input type="text" name="PLACE_OF_RECEIPT_CUSTOMIZED" value={formData?.PLACE_OF_RECEIPT_CUSTOMIZED} onChange={handleInputChange} placeholder="MSC AMALFI" className={inputClass} />
                                        </InputField>
                                        <InputField label="Port of Loading" required>
                                            <SearchBar
                                                searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                                selectReacord={(d) => {
                                                    handleInputChange({ target: { name: 'PORT_OF_LOADING', value: d?.[1] } })
                                                    handleInputChange({ target: { name: 'PORT_OF_LOADING_CODE', value: _getPortCode(d?.[1]) } })
                                                }}
                                                onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'PORT_OF_LOADING', value: '' } }); handleInputChange({ target: { name: 'PORT_OF_LOADING_CODE', value: '' } }); } }}
                                                value={formData?.PORT_OF_LOADING}
                                            />
                                        </InputField>
                                        <InputField label="Show on BL:">
                                            <input type="text" name="PORT_OF_LOADING_CUSTOMIZED" value={formData?.PORT_OF_LOADING_CUSTOMIZED} onChange={handleInputChange} placeholder="2524N" className={inputClass} />
                                        </InputField>
                                        <InputField label="Port of Discharge" required>
                                            <SearchBar
                                                searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                                selectReacord={(d) => {
                                                    handleInputChange({ target: { name: 'PORT_OF_DISCHARGE', value: d?.[1] } })
                                                    handleInputChange({ target: { name: 'PORT_OF_DISCHARGE_CODE', value: _getPortCode(d?.[1]) } })
                                                }}
                                                onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'PORT_OF_DISCHARGE', value: '' } }); handleInputChange({ target: { name: 'PORT_OF_DISCHARGE_CODE', value: '' } }); } }}
                                                value={formData?.PORT_OF_DISCHARGE}
                                            />
                                        </InputField>
                                        <InputField label="Show on BL:">
                                            <input type="text" name="PORT_OF_DISCHARGE_CUSTOMIZED" value={formData?.PORT_OF_DISCHARGE_CUSTOMIZED} onChange={handleInputChange} className={inputClass} />
                                        </InputField>
                                        <InputField label="Place of Delivery">
                                            <SearchBar
                                                searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                                selectReacord={(d) => {
                                                    handleInputChange({ target: { name: 'PLACE_OF_DELIVERY', value: d?.[1] } })
                                                    handleInputChange({ target: { name: 'PLACE_OF_DELIVERY_CODE', value: _getPortCode(d?.[1]) } })
                                                }}
                                                onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'PLACE_OF_DELIVERY', value: '' } }); handleInputChange({ target: { name: 'PLACE_OF_DELIVERY_CODE', value: '' } }); } }}
                                                value={formData?.PLACE_OF_DELIVERY}
                                            />
                                        </InputField>
                                        <InputField label="Show on BL:">
                                            <input type="text" name="PLACE_OF_DELIVERY_CUSTOMIZED" value={formData?.PLACE_OF_DELIVERY_CUSTOMIZED} onChange={handleInputChange} className={inputClass} />
                                        </InputField>
                                    </div>
                                </motion.div>
                            )}
                        </SectionCard>

                        <SectionCard>
                            <SectionHeader
                                icon={Route}
                                title="Routing Information"
                                color="cyan"
                                section="routing"
                                expandedSections={expandedSections}
                                toggleSection={toggleSection}
                            />
                            {expandedSections.routing && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    className="p-5"
                                >
                                    <div className="py-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                        <InputField label="Vessel" required>
                                            <SearchBar
                                                searchAPI={'v1/AutoSuggestion/GetVesselList?Prefix='}
                                                selectReacord={(d) => {
                                                    handleInputChange({ target: { name: 'VESSEL_NAME', value: d?.[1] } });
                                                    handleInputChange({ target: { name: 'VESSEL_ID', value: String(d?.[0] ?? "") } });
                                                }}
                                                onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'VESSEL_NAME', value: '' } }); handleInputChange({ target: { name: 'VESSEL_ID', value: 0 } }); } }}
                                                value={formData?.VESSEL_NAME}
                                            />
                                        </InputField>
                                        <InputField label="Voyage" required>
                                            <input type="text" name="VOYAGE" value={formData?.VOYAGE} onChange={handleInputChange} placeholder="" className={inputClass} />
                                        </InputField>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
                                        <InputField label="Pre-Carrage By:">
                                            <input type="text" name="PRE_CARRIAGE_BY" value={formData?.PRE_CARRIAGE_BY} onChange={handleInputChange} placeholder="" className={inputClass} />
                                        </InputField>
                                        <InputField label="Country of Origin">
                                            <input type="text" name="COUNTRY_OF_ORIGIN" value={formData?.COUNTRY_OF_ORIGIN} onChange={handleInputChange} placeholder="" className={inputClass} />
                                        </InputField>
                                        <InputField label="Loading Pier / Terminal:">
                                            <input type="text" name="LOADING_PIER_TERMINAL" value={formData?.LOADING_PIER_TERMINAL} onChange={handleInputChange} className={inputClass} />
                                        </InputField>
                                        <InputField label="Move Type" required>
                                            <select name="MOVE_TYPE" value={formData?.MOVE_TYPE || ''} onChange={handleInputChange} className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]">
                                                <option value="">SELECT TYPE</option>
                                                {
                                                    moveTypeList.map((item, index) => (
                                                        <option key={index} value={item.moveType}>{item.moveType}</option>
                                                    ))
                                                }
                                            </select>
                                        </InputField>
                                        <InputField label="Carrier" required>
                                            <SearchBar
                                                searchAPI={'v1/AutoSuggestion/GetCarrierSuggestion?Prefix='}
                                                selectReacord={(d) => {
                                                    handleInputChange({ target: { name: 'CARRIER_NAME', value: d?.[1] } });
                                                    handleInputChange({ target: { name: 'CARRIER_ID', value: d?.[0] } });
                                                    handleInputChange({ target: { name: 'CARRIER_SCAC', value: String(d?.[2] ?? '') } });
                                                }}
                                                onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'CARRIER_NAME', value: '' } }); handleInputChange({ target: { name: 'CARRIER_ID', value: 0 } }); } }}
                                                value={formData?.CARRIER_NAME}
                                            />
                                        </InputField>
                                    </div>
                                </motion.div>
                            )}
                        </SectionCard>
                    </div>

                    {/* Particulars Furnished by Shipper */}
                    <SectionCard ref={particularsRef}>
                        <SectionHeader
                            icon={Package}
                            title="Particulars Furnished by Shipper"
                            color="orange"
                            section="containers"
                            expandedSections={expandedSections}
                            toggleSection={toggleSection}
                        />
                        {expandedSections.containers && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="p-4 space-y-4">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                                                <Settings className="w-4 h-4 text-white" />
                                            </div>
                                            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Print Options</h4>
                                        </div>
                                        <div className="space-y-2">
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Weight & Measurement on B/L</p>
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    { value: 'B', label: 'Both' },
                                                    { value: 'M', label: 'Metric' },
                                                    { value: 'E', label: 'English' }
                                                ].map(opt => (
                                                    <label key={opt.value} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all ${formData?.WEIGHT_PRINT_OPTION === opt.value ? 'bg-blue-500 text-white shadow-md' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:border-blue-300'}`}>
                                                        <input type="radio" name="WEIGHT_PRINT_OPTION" value={opt.value} checked={formData?.WEIGHT_PRINT_OPTION === opt.value} onChange={handleInputChange} className="sr-only" />
                                                        {opt.label}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                                                <Hash className="w-4 h-4 text-white" />
                                            </div>
                                            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Calculation</h4>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-700/50 transition-colors cursor-pointer">
                                                <input type="checkbox" name='DO_NOT_AUTO_CALCULATE' checked={formData?.DO_NOT_AUTO_CALCULATE} onChange={handleInputChange} className="w-4 h-4 text-emerald-500 rounded border-slate-300 focus:ring-emerald-500" />
                                                <span className="text-xs text-slate-600 dark:text-slate-300">Do not auto calculate</span>
                                            </label>
                                            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-700/50 transition-colors cursor-pointer">
                                                <input type="checkbox" name='DO_NOT_PRINT_NET_WEIGHT' checked={formData?.DO_NOT_PRINT_NET_WEIGHT} onChange={handleInputChange} className="w-4 h-4 text-emerald-500 rounded border-slate-300 focus:ring-emerald-500" />
                                                <span className="text-xs text-slate-600 dark:text-slate-300">Do not print Net Weight</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
                                                <Printer className="w-4 h-4 text-white" />
                                            </div>
                                            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Print Format</h4>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-700/50 transition-colors cursor-pointer">
                                                <input type="checkbox" name='IS_PRINT_GROUP_COMMODITIES' checked={formData?.IS_PRINT_GROUP_COMMODITIES} onChange={handleInputChange} className="w-4 h-4 text-purple-500 rounded border-slate-300 focus:ring-purple-500" />
                                                <span className="text-xs text-slate-600 dark:text-slate-300">Print in Group Commodities</span>
                                            </label>
                                            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-700/50 transition-colors cursor-pointer">
                                                <input type="checkbox" name='IS_PRINT_WITH_RIDER_LIST' checked={formData?.IS_PRINT_WITH_RIDER_LIST} onChange={handleInputChange} className="w-4 h-4 text-purple-500 rounded border-slate-300 focus:ring-purple-500" />
                                                <span className="text-xs text-slate-600 dark:text-slate-300">Print with RIDER LIST</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mr-2">Import:</span>
                                        <button type="button" onClick={() => navigate('/booking')} className="px-3 py-1.5 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-lg text-xs font-medium transition-all shadow-sm hover:shadow-md flex items-center gap-1.5">
                                            <Upload className="w-3 h-3" /> Dock Receipt
                                        </button>
                                        <button type="button" className="px-3 py-1.5 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-lg text-xs font-medium transition-all shadow-sm hover:shadow-md flex items-center gap-1.5">
                                            <Upload className="w-3 h-3" /> HBL
                                        </button>
                                        <button type="button" onClick={() => navigate('/booking')} className="px-3 py-1.5 bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-600 hover:to-blue-600 text-white rounded-lg text-xs font-medium transition-all shadow-sm hover:shadow-md flex items-center gap-1.5">
                                            <Package className="w-3 h-3" /> Booking Cargo
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <button type="button" className="px-3 py-1.5 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-medium transition-all border border-slate-200 dark:border-slate-600 flex items-center gap-1.5">
                                            <FileText className="w-3 h-3" /> Fill Description
                                        </button>
                                        <button type="button" className="px-3 py-1.5 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-medium transition-all border border-slate-200 dark:border-slate-600 flex items-center gap-1.5">
                                            <Settings className="w-3 h-3" /> Override
                                        </button>
                                    </div>
                                </div>

                                <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                                    <div className="grid bg-gradient-to-r from-slate-700 to-slate-800 dark:from-slate-800 dark:to-slate-900 text-white text-xs font-semibold" style={{ gridTemplateColumns: '120px 100px 160px 1fr 120px 120px 120px 60px' }}>
                                        <div className="px-3 py-3 text-center border-r border-slate-600">CONTAINER</div>
                                        <div className="px-3 py-3 text-center border-r border-slate-600">CONT. TYPE</div>
                                        <div className="px-3 py-3 text-center border-r border-slate-600">NO. OF PKG</div>
                                        <div className="px-3 py-3 text-center border-r border-slate-600">CARGO DESCRIPTION</div>
                                        <div className="px-3 py-3 text-center border-r border-slate-600">NET WEIGHT</div>
                                        <div className="px-3 py-3 text-center border-r border-slate-600">GROSS WEIGHT</div>
                                        <div className="px-3 py-3 text-center border-r border-slate-600">MEASUREMENT</div>
                                        <div className="px-3 py-3 text-center">DEL</div>
                                    </div>

                                    <div className="overflow-x-auto bg-white dark:bg-slate-900">
                                        <div className="min-w-[1000px]">
                                            {rows.map((row, index) => row?.IS_DELETED == false && (
                                                <div key={index} className="grid border-t border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors" style={{ gridTemplateColumns: '120px 100px 160px 1fr 120px 120px 120px 60px' }}>
                                                    <div className="p-2 space-y-1.5 border-r border-slate-100 dark:border-slate-700/50">
                                                        <input
                                                            value={row?.CONTAINER_NUMBER}
                                                            onChange={(e) => _handleInputChange(index, 'CONTAINER_NUMBER', e.target.value)}
                                                            placeholder="Container No"
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                        <input
                                                            value={row?.SEAL_NUMBER}
                                                            onChange={(e) => _handleInputChange(index, 'SEAL_NUMBER', e.target.value)}
                                                            placeholder="Seal No"
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                        <textarea
                                                            rows={3}
                                                            value={row?.MARKS_NO}
                                                            onChange={(e) => _handleInputChange(index, 'MARKS_NO', e.target.value)}
                                                            placeholder="Marks No"
                                                            className="w-full min-h-[70px] resize-y rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                    </div>

                                                    <div className="p-2 space-y-1.5 border-r border-slate-100 dark:border-slate-700/50">
                                                        <select
                                                            value={row?.CONT_TYPE_CODE || ''}
                                                            onChange={(e) => {
                                                                _handleInputChange(index, 'CONT_TYPE_CODE', e.target.value)
                                                                _handleInputChange(index, 'CONT_TYPE', e.target.options[e.target.selectedIndex].text)
                                                            }}
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        >
                                                            <option value="">Select</option>
                                                            {
                                                                containerTypeList?.map((container, key) => (
                                                                    <option key={key} value={container.moveTypeId}>{container.moveType}</option>
                                                                ))
                                                            }
                                                        </select>
                                                        <input type='number'
                                                            min="0"
                                                            value={row.TARE_WEIGHT}
                                                            onChange={(e) => _handleInputChange(index, 'TARE_WEIGHT', e.target.value)}
                                                            placeholder="Tare Weight"
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                    </div>

                                                    <div className="p-2 space-y-1.5 border-r border-slate-100 dark:border-slate-700/50">
                                                        <div className="grid grid-cols-2 gap-1">
                                                            <input
                                                                value={row.NO_PACKAGE}
                                                                onChange={(e) => _handleInputChange(index, 'NO_PACKAGE', e.target.value)}
                                                                placeholder="Qty"
                                                                className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                            />
                                                            <select
                                                                value={row.PACKAGE_CODE}
                                                                onChange={(e) => _handleInputChange(index, 'PACKAGE_CODE', e.target.value)}
                                                                className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-1 text-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                            >
                                                                <option value="">Type</option>
                                                                <option value="CARTON">CTN</option>
                                                                <option value="PALLET">PLT</option>
                                                                <option value="CRATE">CRT</option>
                                                                <option value="DRUM">DRM</option>
                                                                <option value="BAG">BAG</option>
                                                            </select>
                                                        </div>
                                                        <input
                                                            value={row.HAZ_CODE}
                                                            onChange={(e) => _handleInputChange(index, 'HAZ_CODE', e.target.value)}
                                                            placeholder="HS Code"
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                        <input
                                                            value={row.VIN_NUMBER}
                                                            onChange={(e) => _handleInputChange(index, 'VIN_NUMBER', e.target.value)}
                                                            placeholder="VIN Number"
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                        <button type="button" className="w-full h-7 rounded-md bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-[10px] font-medium transition-all shadow-sm flex items-center justify-center gap-1">
                                                            <Check className="w-3 h-3" /> Validate VIN
                                                        </button>
                                                        <div className="grid grid-cols-2 gap-1">
                                                            <input
                                                                value={row.TITLE_NUMBER}
                                                                onChange={(e) => _handleInputChange(index, 'TITLE_NUMBER', e.target.value)}
                                                                placeholder="Title No"
                                                                className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                            />
                                                            <input
                                                                value={row.CARGO_STATE}
                                                                onChange={(e) => _handleInputChange(index, 'CARGO_STATE', e.target.value)}
                                                                placeholder="State"
                                                                className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="p-2 border-r border-slate-100 dark:border-slate-700/50">
                                                        <textarea
                                                            rows={8}
                                                            value={row.DESCRIPTIONS}
                                                            onChange={(e) => _handleInputChange(index, 'DESCRIPTIONS', e.target.value)}
                                                            placeholder="Enter detailed cargo description here...&#10;&#10;Example:&#10;- 100 CARTONS OF ELECTRONICS&#10;- HS CODE: 8471.30&#10;- MADE IN CHINA"
                                                            className="w-full h-full min-h-[180px] resize-y rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-3 py-2 text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                    </div>

                                                    <div className="p-2 space-y-2 border-r border-slate-100 dark:border-slate-700/50">
                                                        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2 space-y-1.5">
                                                            <label className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Net KGS</label>
                                                            <input
                                                                value={row.NET_KGS}
                                                                onChange={(e) => handleWeightChange(index, 'NET_KGS', e.target.value)}
                                                                className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                            />
                                                        </div>
                                                        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2 space-y-1.5">
                                                            <label className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Net LBS</label>
                                                            <input
                                                                value={row.NET_LBS}
                                                                onChange={(e) => handleWeightChange(index, 'NET_LBS', e.target.value)}
                                                                className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="p-2 space-y-2 border-r border-slate-100 dark:border-slate-700/50">
                                                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 space-y-1.5">
                                                            <label className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">KGS</label>
                                                            <input
                                                                value={row.KGS}
                                                                onChange={(e) => handleWeightChange(index, 'KGS', e.target.value)}
                                                                className="w-full h-7 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                            />
                                                        </div>
                                                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 space-y-1.5">
                                                            <label className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">LBS</label>
                                                            <input
                                                                value={row.LBS}
                                                                onChange={(e) => handleWeightChange(index, 'LBS', e.target.value)}
                                                                className="w-full h-7 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="p-2 space-y-2 border-r border-slate-100 dark:border-slate-700/50">
                                                        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 space-y-1.5">
                                                            <label className="text-[10px] font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">CBM</label>
                                                            <input
                                                                value={row.CBM}
                                                                onChange={(e) => handleWeightChange(index, 'CBM', e.target.value)}
                                                                className="w-full h-7 rounded-md border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                            />
                                                        </div>
                                                        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 space-y-1.5">
                                                            <label className="text-[10px] font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">CFT</label>
                                                            <input
                                                                value={row.CFT}
                                                                onChange={(e) => handleWeightChange(index, 'CFT', e.target.value)}
                                                                className="w-full h-7 rounded-md border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="p-2 flex items-center justify-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => { _handleInputChange(index, 'IS_DELETED', true) }}
                                                            className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 hover:text-red-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                                            disabled={rows.length === 1}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 py-3 flex justify-center border-t border-slate-200 dark:border-slate-700">
                                        <button
                                            type="button"
                                            onClick={addRow}
                                            className="px-5 py-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-lg font-medium text-sm transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                                        >
                                            <Plus className="w-4 h-4" /> Add Container Row
                                        </button>
                                    </div>
                                </div>

                                <div className="bg-gradient-to-br from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                                            <Weight className="w-4 h-4 text-white" />
                                        </div>
                                        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Total Summary</h4>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                            <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">Containers</p>
                                            <p className="text-lg font-bold text-slate-800 dark:text-slate-200">{formData?.TOTAL_CONTAINER || '0'}</p>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                            <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">Packages</p>
                                            <p className="text-lg font-bold text-slate-800 dark:text-slate-200">{formData?.TOTAL_PACKAGE || '0'}</p>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                            <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">Gross KGS</p>
                                            <p className="text-lg font-bold text-slate-800 dark:text-slate-200">{formData?.TOTAL_KGS || '0.00'}</p>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                            <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">Gross LBS</p>
                                            <p className="text-lg font-bold text-slate-800 dark:text-slate-200">{formData?.TOTAL_LBS || '0.00'}</p>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                            <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">Net KGS</p>
                                            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formData?.TOTAL_NET_KGS || '0.00'}</p>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                            <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">Net LBS</p>
                                            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formData?.TOTAL_NET_LBS || '0.00'}</p>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                            <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">CBM</p>
                                            <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{formData?.TOTAL_CBM || '0.00'}</p>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                            <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">CFT</p>
                                            <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{formData?.TOTAL_CFT || '0.00'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/10 dark:to-orange-900/10 rounded-xl border border-amber-200 dark:border-amber-800/30">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                                            <FileText className="w-4 h-4 text-white" />
                                        </div>
                                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Show L/C or Manual Particulars Furnished by Shipper</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <label className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all ${formData?.IS_LC_MANUAL_DETAILS === true ? 'bg-amber-500 text-white shadow-md' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:border-amber-300'}`}>
                                            <input type="radio" name="IS_LC_MANUAL_DETAILS" value='true' checked={formData?.IS_LC_MANUAL_DETAILS === true} onChange={(e) => handleInputChange({ target: { name: e.target.name, value: true } })} className="sr-only" />
                                            Yes
                                        </label>
                                        <label className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all ${formData?.IS_LC_MANUAL_DETAILS === false ? 'bg-slate-500 text-white shadow-md' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:border-slate-400'}`}>
                                            <input type="radio" name="IS_LC_MANUAL_DETAILS" value='false' checked={formData?.IS_LC_MANUAL_DETAILS === false} onChange={(e) => handleInputChange({ target: { name: e.target.name, value: false } })} className="sr-only" />
                                            No
                                        </label>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </SectionCard>

                    {/* Charges: Additional Information + Individual Charges */}
                    <div ref={chargesRef} className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        <SectionCard>
                            <SectionHeader
                                icon={Info}
                                title="Additional Information"
                                color="teal"
                                section="additional"
                                expandedSections={expandedSections}
                                toggleSection={toggleSection}
                            />
                            {expandedSections.additional && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    className="p-5"
                                >
                                    <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-200 dark:border-teal-800">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                            <InputField label="Additional Address:">
                                                <input type="text" name="ADDITIONAL_INFO" value={formData?.ADDITIONAL_INFO} onChange={handleInputChange} placeholder="Enter..." className={inputClass} />
                                            </InputField>
                                            <InputField label="ACID Number:">
                                                <input type="text" name="ACID_NUMBER" value={formData?.ACID_NUMBER} onChange={handleInputChange} placeholder="" className={inputClass} />
                                            </InputField>
                                            <InputField label="Importer VAT Number:">
                                                <input type="text" name="IMPORTER_VAT_NUMBER" value={formData?.IMPORTER_VAT_NUMBER} onChange={handleInputChange} className={inputClass} />
                                            </InputField>
                                            <InputField label="Exporter Registration Number:">
                                                <input type="text" name="EXPORTER_REGISTRATION_NUMBER" value={formData?.EXPORTER_REGISTRATION_NUMBER} onChange={handleInputChange} className={inputClass} />
                                            </InputField>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </SectionCard>

                        <SectionCard>
                            <SectionHeader
                                icon={Ship}
                                title="Individual Charges"
                                color="cyan"
                                section="charges"
                                expandedSections={expandedSections}
                                toggleSection={toggleSection}
                            />
                            {expandedSections.charges && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    className="p-5"
                                >
                                    <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            <InputField label="Ocean Freight">
                                                <select name="OCEAN_FREIGHT_PAY_MODE" value={formData?.OCEAN_FREIGHT_PAY_MODE || ''} onChange={handleInputChange} className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]">
                                                    <option value="N">NONE</option>
                                                    <option value="C">Collect</option>
                                                    <option value="P">Prepaid</option>
                                                </select>
                                            </InputField>
                                            <InputField label="Origin Port Charges(THC)">
                                                <select name="OPC_PAY_MODE" value={formData?.OPC_PAY_MODE || ''} onChange={handleInputChange} className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]">
                                                    <option value="N">NONE</option>
                                                    <option value="C">Collect</option>
                                                    <option value="P">Prepaid</option>
                                                </select>
                                            </InputField>
                                            <InputField label="Origin Haulage Charges">
                                                <select name="OHC_PAY_MODE" value={formData?.OHC_PAY_MODE || ''} onChange={handleInputChange} className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]">
                                                    <option value="N">NONE</option>
                                                    <option value="C">Collect</option>
                                                    <option value="P">Prepaid</option>
                                                </select>
                                            </InputField>
                                            <InputField label="Additional Charges">
                                                <select name="AC_PAY_MODE" value={formData?.AC_PAY_MODE || ''} onChange={handleInputChange} className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]">
                                                    <option value="N">NONE</option>
                                                    <option value="C">Collect</option>
                                                    <option value="P">Prepaid</option>
                                                </select>
                                            </InputField>
                                            <InputField label="Destination Port Charges(THC)">
                                                <select name="DPC_PAY_MODE" value={formData?.DPC_PAY_MODE || ''} onChange={handleInputChange} className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]">
                                                    <option value="N">NONE</option>
                                                    <option value="C">Collect</option>
                                                    <option value="P">Prepaid</option>
                                                </select>
                                            </InputField>
                                            <InputField label="Destination Haulage Charges">
                                                <select name="DHC_PAY_MODE" value={formData?.DHC_PAY_MODE || ''} onChange={handleInputChange} className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]">
                                                    <option value="N">NONE</option>
                                                    <option value="C">Collect</option>
                                                    <option value="P">Prepaid</option>
                                                </select>
                                            </InputField>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </SectionCard>
                    </div>

                    {/* Payment & Print: Payment Terms, Release Mode, INTTRA Cards, Remarks */}
                    <div ref={paymentPrintRef} className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <SectionCard>
                                <SectionHeader
                                    icon={CreditCard}
                                    title="Payment Terms"
                                    color="pink"
                                    section="payment"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.payment && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="p-5"
                                    >
                                        <div className="mb-4">
                                            <div className="flex gap-4">
                                                {[
                                                    { label: 'Prepaid', value: 'P' },
                                                    { label: 'Collect', value: 'C' },
                                                    { label: 'No Status', value: 'N' },
                                                ].map((item) => (
                                                    <label
                                                        key={item.value}
                                                        className={`flex-1 p-4 rounded-xl border-2 cursor-pointer transition-all
                                                            ${formData?.MBL_PAYMENT_MODE === item.value.trimEnd()
                                                                ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20'
                                                                : 'border-slate-200 dark:border-slate-700 hover:border-pink-300'
                                                            }`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div
                                                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                                                                    ${formData?.MBL_PAYMENT_MODE === item.value.trimEnd()
                                                                        ? 'border-pink-500 bg-pink-500'
                                                                        : 'border-slate-300'
                                                                    }`}
                                                            >
                                                                {formData?.MBL_PAYMENT_MODE === item.value.trimEnd() && (
                                                                    <Check className="w-3 h-3 text-white" />
                                                                )}
                                                            </div>
                                                            <span className="font-medium text-slate-900 dark:text-white">
                                                                {item.label}
                                                            </span>
                                                        </div>
                                                        <input
                                                            type="radio"
                                                            name="MBL_PAYMENT_MODE"
                                                            value={item.value}
                                                            checked={formData?.MBL_PAYMENT_MODE === item.value.trimEnd()}
                                                            onChange={handleInputChange}
                                                            className="fixed opacity-0 pointer-events-none"
                                                        />
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="mb-4">
                                            <InputField label="Payment Mode">
                                                <input type="text" name="PAYMENT_MODE" value={formData?.PAYMENT_MODE} onChange={handleInputChange} className={inputClass} />
                                            </InputField>
                                            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap mt-2">
                                                <input
                                                    type="checkbox"
                                                    name="PAYMENT_MODE_HIDE"
                                                    checked={formData?.PAYMENT_MODE_HIDE}
                                                    className="h-4 w-4 text-blue-600 border-gray-300"
                                                    onChange={handleInputChange}
                                                />
                                                Hide on BL
                                            </label>
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>

                            <SectionCard>
                                <SectionHeader
                                    icon={CreditCard}
                                    title="Release Mode"
                                    color="green"
                                    section="release"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.release && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="p-5"
                                    >
                                        <div className="mb-4">
                                            <div className="flex gap-4">
                                                {[
                                                    { label: 'Original', value: 'O' },
                                                    { label: 'Express', value: 'E' },
                                                    { label: 'Telex Release', value: 'R' },
                                                ].map((item) => (
                                                    <label
                                                        key={item.value}
                                                        className={`flex-1 p-4 rounded-xl border-2 cursor-pointer transition-all
                                                            ${formData?.MBL_RELEASE_MODE === item.value
                                                                ? 'border-lime-500 bg-lime-50 dark:bg-lime-900/20'
                                                                : 'border-slate-200 dark:border-slate-700 hover:border-lime-300'
                                                            }`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div
                                                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                                                                    ${formData?.MBL_RELEASE_MODE === item.value
                                                                        ? 'border-lime-500 bg-lime-500'
                                                                        : 'border-slate-300'
                                                                    }`}
                                                            >
                                                                {formData?.MBL_RELEASE_MODE === item.value && (
                                                                    <Check className="w-3 h-3 text-white" />
                                                                )}
                                                            </div>
                                                            <span className="font-medium text-slate-900 dark:text-white">
                                                                {item.label}
                                                            </span>
                                                        </div>
                                                        <input
                                                            type="radio"
                                                            name="MBL_RELEASE_MODE"
                                                            value={item.value}
                                                            checked={formData?.MBL_RELEASE_MODE === item.value}
                                                            onChange={handleInputChange}
                                                            className="fixed opacity-0 pointer-events-none"
                                                        />
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="mb-4">
                                            <InputField label="Release Mode:">
                                                <input type="text" name="numberOfOriginals" className={inputClass} />
                                            </InputField>
                                            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap mt-2">
                                                <input
                                                    type="checkbox"
                                                    name="RELEASE_MODE_HIDE"
                                                    checked={formData?.RELEASE_MODE_HIDE}
                                                    className="h-4 w-4 text-blue-600 border-gray-300"
                                                    onChange={handleInputChange}
                                                />
                                                Hide on BL
                                            </label>
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <SectionCard>
                                <SectionHeader
                                    icon={CreditCard}
                                    title="Additional Information for INTTRA Purpose"
                                    color="blue"
                                    section="inttra1"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.inttra1 && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="p-5"
                                    >
                                        <div className="p-4 bg-blue-100 dark:bg-blue-400/20 rounded-xl border border-blue-200 dark:border-blue-800">
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                                <InputField label="Cargo Value Declared:">
                                                    <input type="text" name="CARGO_DEC_VALUE" value={formData?.CARGO_DEC_VALUE} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Shipment Type:">
                                                    <select name="SHIPMENT_TYPE" value={formData?.SHIPMENT_TYPE} onChange={(e) =>
                                                        handleInputChange({
                                                            target: {
                                                                name: "SHIPMENT_TYPE",
                                                                value: e.target.value,
                                                            },
                                                        })
                                                    } className={selectClass}>
                                                        <option value="9">Original</option>
                                                        <option value="1">Cancel</option>
                                                        <option value="5">Change</option>
                                                    </select>
                                                </InputField>
                                                <InputField label="Letter Of Credit Number:">
                                                    <input type="text" name="LETTER_OF_CREDIT_NUMBER" value={formData?.LETTER_OF_CREDIT_NUMBER} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="L/C Issue Date:">
                                                    <DateInputwithcal onError={handleDateError} label="L/C Issue Date:" name="LC_ISSUE_DATE" value={formData?.LC_ISSUE_DATE} onChange={handleDateChange} required />
                                                </InputField>
                                                <InputField label="L/C Expire Date:">
                                                    <DateInputwithcal onError={handleDateError} label="L/C Expire Date:" name="LC_EXPIRE_DATE" value={formData?.LC_EXPIRE_DATE} onChange={handleDateChange} required />
                                                </InputField>
                                                <InputField label="Export License Number:">
                                                    <input type="text" name="EXPORT_LIC_NUMBER" value={formData?.EXPORT_LIC_NUMBER} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="General Information:">
                                                    <Textarea row='2' type="text" name="GENERAL_INFORMATION" value={formData?.GENERAL_INFORMATION} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Bill of Lading Clasues:">
                                                    <Textarea row='2' type="text" name="BILL_OF_LADING_CLAUSE" value={formData?.BILL_OF_LADING_CLAUSE} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Customs House Broker Reference No.:">
                                                    <input type="text" name="CUSTOM_HOUSE_BROKER" value={formData?.CUSTOM_HOUSE_BROKER} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>

                            <SectionCard>
                                <SectionHeader
                                    icon={CreditCard}
                                    title="Additional Information for INTTRA Purpose"
                                    color="indigo"
                                    section="inttra2"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.inttra2 && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="p-5"
                                    >
                                        <div className="p-4 bg-blue-50 dark:bg-blue-800/20 rounded-xl border border-blue-200 dark:border-blue-800">
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                                <InputField label="No. Original (Print):">
                                                    <input type="number" min="0" name="NO_ORIGINAL" value={formData?.NO_ORIGINAL} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Original B/L (Freighted):">
                                                    <input type="number" min="0" name="ORIGINAL_BL_FR" value={formData?.ORIGINAL_BL_FR} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Original B/L (Non-Freighted):">
                                                    <input type="number" min="0" name="ORIGINAL_BL_NON_FR" value={formData?.ORIGINAL_BL_NON_FR} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Non Nego. B/L (Freighted):">
                                                    <input type="number" min="0" name="NON_NEGO_BL_FR" value={formData?.NON_NEGO_BL_FR} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Non Nego. B/L (Non-Freighted):">
                                                    <input type="number" min="0" name="NON_NEGO_BL_NON_FR" value={formData?.NON_NEGO_BL_NON_FR} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="WayBill Print (Freighted):">
                                                    <input type="number" min="0" name="WAYBILL_PRINT_FREIGHTED" value={formData?.WAYBILL_PRINT_FREIGHTED} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="WayBill Print (Non-Freighted):">
                                                    <input type="number" min="0" name="WAYBILL_PRINT" value={formData?.WAYBILL_PRINT} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Draft Proof Copy Print:">
                                                    <input type="number" min="0" name="DRAFT_PROOF" value={formData?.DRAFT_PROOF} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="On-Board (For Original):">
                                                    <input type="checkbox" name="ON_BOARD_PRINT" value={formData?.ON_BOARD_PRINT} checked={formData?.ON_BOARD_PRINT} onChange={handleInputChange} className={inputClass} />
                                                </InputField>
                                                <InputField label="Freight Payable at:">
                                                    <SearchBar
                                                        searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                                        selectReacord={(d) => {
                                                            handleInputChange({ target: { name: 'FREIGHT_PAYABLE', value: d?.[1] } })
                                                            handleInputChange({ target: { name: 'FREIGHT_PAYABLE_CODE', value: d?.[0] } })
                                                        }}
                                                        onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'FREIGHT_PAYABLE', value: '' } }); handleInputChange({ target: { name: 'FREIGHT_PAYABLE_CODE', value: '' } }); } }}
                                                        value={formData?.FREIGHT_PAYABLE}
                                                    />
                                                </InputField>
                                                <InputField label="Place Of Issue :">
                                                    <SearchBar
                                                        searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                                        selectReacord={(d) => {
                                                            handleInputChange({ target: { name: 'PLACE_OF_ISSUE', value: d?.[1] } })
                                                            handleInputChange({ target: { name: 'PLACE_OF_ISSUE_CODE', value: d?.[0]?.toString() } })
                                                        }}
                                                        onchange={(val) => { if (!val) { handleInputChange({ target: { name: 'PLACE_OF_ISSUE', value: '' } }); handleInputChange({ target: { name: 'PLACE_OF_ISSUE_CODE', value: '' } }); } }}
                                                        value={formData?.PLACE_OF_ISSUE}
                                                    />
                                                </InputField>
                                                <InputField label="Issue Date:">
                                                    <DateInputwithcal onError={handleDateError} label="L/C Issue Date:" name="ISSUE_DATE" value={formData?.ISSUE_DATE} onChange={handleDateChange} required />
                                                </InputField>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>
                        </div>

                        <SectionCard>
                            <SectionHeader
                                icon={FileText}
                                title="Remarks & Additional Information"
                                color="slate"
                                section="remarks"
                                expandedSections={expandedSections}
                                toggleSection={toggleSection}
                            />
                            {expandedSections.remarks && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    className="p-5"
                                >
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Marks & Numbers</label>
                                            <textarea rows="4" placeholder="Enter marks and numbers..." className={inputClass} />
                                        </div>
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Special Instructions</label>
                                            <textarea rows="4" placeholder="Enter special instructions..." className={inputClass} />
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </SectionCard>

                        {/* Footer */}
                        <div className="p-3 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                    <span className="text-red-500">*</span> Required fields must be filled
                                </div>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 text-slate-600 hover:bg-red-50 dark:text-slate-300 dark:hover:bg-red-900/20"
                                    >
                                        <X className="w-[15px] h-[15px]" />
                                        <span className="hidden sm:inline">Cancel</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate('/mblPrint', { state: { bookingId: bookingId, FILENUMBER: FILENUMBER } });
                                        }}
                                        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600"
                                    >
                                        <Printer className="w-[15px] h-[15px]" />
                                        <span className="hidden sm:inline">Print</span>
                                    </button>
                                    <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />
                                    <button
                                        type="button"
                                        onClick={handleSave}
                                        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-5 h-[40px] rounded-[9px] transition-all duration-150 bg-green-600 text-white hover:bg-green-700 shadow-sm hover:shadow-green-500/30"
                                    >
                                        <Send className="w-[15px] h-[15px]" />
                                        <span>Submit MBL</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </>
    );
};

export default MBL;